/**
 * The map's terrain layer — the region's roads, rivers, canals and field
 * boundaries as hairlines, plus its water as filled area — read off exactly the
 * data the 3D view draws them from.
 *
 * **Where the network actually lives in the data, measured.** The obvious
 * answer is a `.TBL` object's 2-point faces — `three/polygon-outlines.ts` calls
 * them "roads, rivers, field boundaries, rigging" — and it is the wrong one at
 * map scale. Every line-face-bearing object a region places is *small*: a
 * bounding radius of at most 534 m, 209–308 placed segments across a whole
 * region, and a total inked length of **9 pixels** on Verdun's sheet. Those are
 * kerbs and hedges beside a village, not a river.
 *
 * The real network is the handful of **region-spanning flat triangle objects**:
 * 6–15 per region, each placed exactly once, each with a bounding radius
 * between 10.3 km and 29 km against a largest-non-network object of 7.3 km
 * (Paris) or 534 m (everywhere else). Those are the rivers, roads and the
 * coastline, and the 3D view draws them as hairlines the same way — through
 * `triangleOutlineSegments`, because a river in this engine is a long thin
 * quad strip lying on the ground rather than a polyline
 * (docs/formats/gi-format.md §6.1/§9's "a river really is 40–86 km long").
 *
 * So the sheet outlines those, and keeps the line faces as well since they cost
 * nothing. Everything smaller is deliberately left out: a building's edges are
 * sub-pixel noise at 190 pixels to 66 miles, and the thing a pilot matches
 * against the ground below is the network — precisely the part the original's
 * own briefing maps drew (`MAPn.DAT` is front-line and town vector art in this
 * same coordinate system, docs/formats/world-terrain.md §2.5).
 *
 * **Each run carries what it is.** Every segment leaves here tagged with a
 * `MapFeature` taken from the face's own `BARON.PAL` index, so the sheet can
 * ink a river blue and a road near-black instead of drawing both in the same
 * brown. The classification and its evidence are `map-features.ts`; this module
 * only threads it through, and the ordering contract it relies on is stated at
 * `objectDraw`.
 *
 * **And the water comes back as area.** The two `s = 10` sea plates are
 * excluded from the outline pass (`NETWORK_MAX_RADIUS_M`) and re-enter through
 * `map-water.ts` as filled polygons — see that module for why the same object
 * is unusable one way round and the best mark on the sheet the other.
 *
 * The model JSONs are the ones `/fly` has already fetched to build the scenery,
 * so opening the map re-reads them from the HTTP cache rather than the network.
 */

import { bestDrawableLod, loadBakedModel, type BakedModelBody } from "../three/baked-model.ts";
import { buildGeometryAttributes } from "../three/model-geometry.ts";
import { lineFaceSegments, triangleOutlineSegments } from "../three/polygon-outlines.ts";
import { modelFrameToWorld } from "../world/placement-transform.ts";
import type { PlacementOrigin } from "../world/placement-transform.ts";
import type { WorldPlacementBody } from "../world/region-loader.ts";
import { featureOfPaletteIndex, WATER_PALETTE_INDEX, type MapFeature } from "./map-features.ts";
import type { MapPoint } from "./map-model.ts";
import { coastlineOf, placementStamp, stampWaterFaces, type MapWater, type MapWaterFace } from "./map-water.ts";

/** One straight run of the network, in map space, and what kind of run it is. */
export interface MapSegment {
  readonly a: MapPoint;
  readonly b: MapPoint;
  readonly feature: MapFeature;
}

/** Everything the sheet's terrain layer draws: the hairline network and the water areas under it. */
export interface MapTerrain {
  readonly segments: readonly MapSegment[];
  readonly water: MapWater;
}

const FLOATS_PER_SEGMENT = 6;

/**
 * The bounding radius above which an object is region geography rather than a
 * building. The census behind it, re-measured over all five regions against the
 * current bake (`unitsPerMetreForScale`, docs/engineering/london-sea-placements.md
 * §8, which made every `scaleExponent > 0` object 2.4765× larger): the largest
 * non-network object is **18.10 km** (Paris `TERR4-19`; 777 m in the other four)
 * and the smallest network one is **25.53 km** (London `TERR1-01`), so this is
 * still a line drawn through a real gap — just a gap that has moved bodily up
 * by 2.4765×, exactly as this constant has (10 km → 25 km). It selects the same
 * 6–15 objects per region it always did, each placed exactly once — the rivers,
 * the roads and, for London and Dunkirk, the coast.
 *
 * Left at 10 km against the current bake it would also sweep in Paris's
 * `TERR4-19/20/21/22` (11.7–18.1 km), which are scenery, not geography.
 *
 * Above `world-network.test.ts`'s own `TYPICAL_RADIUS_CEILING_M` reasoning by
 * the same argument in reverse: that file excludes these objects from a
 * bounding-box check *because* they legitimately span a region. Here they are
 * the only thing worth drawing.
 */
export const NETWORK_MIN_RADIUS_M = 25_000;

/**
 * And the ceiling above it, which exists for exactly two objects in the whole
 * corpus: `TERR1-00` (615 km) and `TERR2-51` (599 km). Those are the pair
 * `world-network.test.ts` already names — `s = 10`, exactly flat (`z ≡ 0` at
 * every vertex), the sea (docs/engineering/lessons.md, 2026-08-23). London's
 * region is 53 km across and Dunkirk's 70 km, so each of these is eight to
 * eleven times its own region; *outlined*, they cover the sheet in long diagonal
 * spokes reaching out to their own far vertices and bury the geography
 * underneath. Every genuine river, road and coastline is at most 108.8 km
 * (`TERR2-15`), so the gap is 2.3× above this line and 2.4× below the sea.
 *
 * **Why this moved from 100 km.** The same 2.4765× bake correction: the sea
 * plates went 248 → 615 km and the longest rivers 43.9 → 108.8 km. At the old
 * 100 km ceiling six river objects (`TERR1-02/03`, `TERR2-15/16`,
 * `TERR3-15/16`) fell *past* it and `isSeaPlate` below then area-filled them,
 * flooding London's, Dunkirk's and Somme's sheets with water where a hairline
 * river belonged.
 *
 * The genuine sea plates are still drawn — as **fill**, through `map-water.ts`,
 * which is the representation an ocean wanted all along.
 */
export const NETWORK_MAX_RADIUS_M = 250_000;

/** Groups a region's placements by the object each names, dropping the unresolved directory slots (`world/object-color.ts` — not a decode error, just nothing to draw). */
function byRef(placements: readonly WorldPlacementBody[]): Map<string, WorldPlacementBody[]> {
  const groups = new Map<string, WorldPlacementBody[]>();
  for (const placement of placements) {
    if (placement.objectRef === null) continue;
    const existing = groups.get(placement.objectRef);
    if (existing) existing.push(placement);
    else groups.set(placement.objectRef, [placement]);
  }
  return groups;
}

/**
 * One object's line segments, already in the model's world-oriented frame
 * (`lineFaceSegments` applies the −90° X fix itself), stamped down at every
 * placement of that object through `map-water.ts`'s shared transform.
 */
function stampSegments(outline: MapOutline, placements: readonly WorldPlacementBody[], origin: PlacementOrigin, into: MapSegment[]): void {
  for (const placement of placements) {
    const stamp = placementStamp(placement, origin);
    for (let segment = 0; segment < outline.segmentCount; segment += 1) {
      const base = segment * FLOATS_PER_SEGMENT;
      const a = stamp(outline.positions[base] ?? 0, outline.positions[base + 2] ?? 0);
      const b = stamp(outline.positions[base + 3] ?? 0, outline.positions[base + 5] ?? 0);
      into.push({ a, b, feature: outline.features[segment] ?? "other" });
    }
  }
}

/**
 * How a placed object's baked model is obtained. Injected so the geometry
 * below is testable against the real bake read off disk, rather than only
 * through `fetch`; `loadMapTerrain` supplies the browser's version.
 */
export type ModelSource = (objectRef: string) => Promise<BakedModelBody | undefined>;

/**
 * Every placed line segment and every water face in the region, in map space.
 * A model the source cannot supply is skipped rather than fatal — the same
 * "skip what fails to load" rule `three/world-instancer.ts` applies to the
 * scenery it could not fetch, so a missing object costs the map one road, not
 * the whole map.
 */
export async function buildMapTerrain(
  placements: readonly WorldPlacementBody[],
  origin: PlacementOrigin,
  source: ModelSource,
): Promise<MapTerrain> {
  const groups = [...byRef(placements)];
  // In parallel, not one after another: a region names 30–57 distinct objects
  // and the map is opened by a key press mid-flight, so a serial round-trip per
  // object is the difference between the sheet appearing and the player
  // watching it load. Matches how `three/world-instancer.ts` builds the same
  // list of refs for the 3D scenery.
  const bodies = await Promise.all(groups.map(([objectRef]) => source(objectRef)));

  const segments: MapSegment[] = [];
  const faces: MapWaterFace[] = [];
  groups.forEach(([, refPlacements], index) => {
    const body = bodies[index];
    if (!body) return;
    const draw = objectDraw(body);
    if (!draw) return;
    if (draw.outline) stampSegments(draw.outline, refPlacements, origin, segments);
    if (draw.water) stampWaterFaces(draw.water.positions, draw.water.triangles, refPlacements, origin, faces);
  });
  return { segments, water: { faces, coastline: coastlineOf(faces) } };
}

/** A tagged run of hairlines in one object's own frame: 6 position floats per segment, one feature per segment. */
interface MapOutline {
  readonly positions: Float32Array;
  readonly segmentCount: number;
  readonly features: readonly MapFeature[];
}

/** What one object contributes to the sheet: its hairlines, tagged, and its water triangles. */
interface ObjectDraw {
  readonly outline: MapOutline | undefined;
  readonly water: { readonly positions: Float32Array; readonly triangles: readonly number[] } | undefined;
}

const EMPTY_OUTLINE: MapOutline = { positions: new Float32Array(0), segmentCount: 0, features: [] };

/**
 * One object's contribution: its 2-point faces always, plus the outline of its
 * triangles when it is big enough to be geography, plus its water triangles
 * whatever its size.
 *
 * `buildGeometryAttributes` hands back model-frame vertices, so each is run
 * through `modelFrameToWorld` — the same −90° X fix `lineFaceSegments` applies
 * to its own positions inline and `three/world-instancer.ts` applies to the
 * shared geometry with `geometry.rotateX`. All three must agree or an object's
 * lines would sit in a different frame from its own edges.
 *
 * **The feature tags ride on an ordering contract**: `triangleOutlineSegments`
 * emits exactly three segments per triangle in triangle order and
 * `lineFaceSegments` exactly one per entry of `lines.colors`, so the tag arrays
 * built here index the segments those two produce — without either side having
 * to carry a per-segment field the 3D path has no use for.
 */
function objectDraw(body: BakedModelBody): ObjectDraw | undefined {
  const lod = bestDrawableLod(body.lods);
  if (!lod) return undefined;
  const lineSource = lineFaceSegments(lod.lines);
  const lines: MapOutline = lineSource
    ? { positions: lineSource.positions, segmentCount: lineSource.segmentCount, features: (lod.lines?.colors ?? []).map((colour) => featureOfPaletteIndex(colour.anchorIndex ?? -1)) }
    : EMPTY_OUTLINE;
  if (lod.triangleCount === 0) return { outline: lines, water: undefined };

  const geography = body.boundingRadius >= NETWORK_MIN_RADIUS_M && body.boundingRadius <= NETWORK_MAX_RADIUS_M;
  const palette = lod.faceColors.map((colour) => colour.anchorIndex ?? -1);
  const waterTriangles = palette.flatMap((index, triangle) => (index === WATER_PALETTE_INDEX ? [triangle] : []));
  if (!geography && waterTriangles.length === 0) return { outline: lines, water: undefined };

  const positions = worldFramePositions(buildGeometryAttributes(lod).positions);
  const water = waterTriangles.length > 0 ? { positions, triangles: waterTriangles } : undefined;
  if (!geography) return { outline: lines, water };
  return { outline: concatOutlines(visibleEdges(positions, palette), lines), water };
}

/** Endpoint quantisation for the shared-edge test, in metres — a millimetre, the same tolerance `three/polygon-outlines.ts` collapses coincident edges at. */
const COINCIDENT_TOLERANCE_M = 0.001;

function cornerKey(positions: Float32Array, at: number): string {
  const quantise = (value: number) => Math.round(value / COINCIDENT_TOLERANCE_M);
  return `${quantise(positions[at] ?? 0)},${quantise(positions[at + 2] ?? 0)}`;
}

/**
 * A triangulated object's edges, minus the ones only the triangulation put
 * there — an edge shared by two faces of the **same** `BARON.PAL` colour.
 *
 * This is the original's own rule read at map scale. `three/polygon-outlines.ts`
 * draws every triangle edge in that face's own colour precisely because such an
 * edge "is invisible against its own fill": in the 3D view the seam between two
 * same-coloured polygons cannot be seen, because the fill either side of it is
 * the same colour. The map draws no fill, so those seams stop being invisible
 * and become lines — the fan diagonals of a 43-triangle landcover plate spraying
 * across a whole region, which is a large part of what "just chaotic lines"
 * (user, 2026-08-23) was looking at.
 *
 * Keying the count on the colour as well as the endpoints is what keeps the
 * information: the boundary between a wood and a field *inside* one plate is a
 * real, visible edge in the original and survives, while the arbitrary split of
 * a single-coloured quad does not.
 */
function visibleEdges(positions: Float32Array, palette: readonly number[]): MapOutline {
  const triangles = Math.floor(positions.length / 9);
  const keys: string[] = [];
  const counts = new Map<string, number>();
  for (let triangle = 0; triangle < triangles; triangle += 1) {
    for (let edge = 0; edge < 3; edge += 1) {
      const first = cornerKey(positions, triangle * 9 + edge * 3);
      const second = cornerKey(positions, triangle * 9 + ((edge + 1) % 3) * 3);
      const key = `${palette[triangle] ?? -1}:${first < second ? `${first}|${second}` : `${second}|${first}`}`;
      keys.push(key);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  const outlines = triangleOutlineSegments({ positions, colors: positions });
  const kept: number[] = [];
  for (let segment = 0; segment < outlines.segmentCount; segment += 1) {
    if (counts.get(keys[segment] ?? "") === 1) kept.push(segment);
  }
  const out = new Float32Array(kept.length * FLOATS_PER_SEGMENT);
  const features: MapFeature[] = [];
  kept.forEach((segment, at) => {
    out.set(outlines.positions.subarray(segment * FLOATS_PER_SEGMENT, (segment + 1) * FLOATS_PER_SEGMENT), at * FLOATS_PER_SEGMENT);
    features.push(featureOfPaletteIndex(palette[Math.floor(segment / 3)] ?? -1));
  });
  return { positions: out, segmentCount: kept.length, features };
}

function concatOutlines(first: MapOutline, second: MapOutline): MapOutline {
  if (second.segmentCount === 0) return first;
  if (first.segmentCount === 0) return second;
  const positions = new Float32Array((first.segmentCount + second.segmentCount) * FLOATS_PER_SEGMENT);
  positions.set(first.positions.subarray(0, first.segmentCount * FLOATS_PER_SEGMENT), 0);
  positions.set(second.positions.subarray(0, second.segmentCount * FLOATS_PER_SEGMENT), first.segmentCount * FLOATS_PER_SEGMENT);
  return { positions, segmentCount: first.segmentCount + second.segmentCount, features: [...first.features, ...second.features] };
}

/** Non-indexed triangle corners moved from the model's own frame into the world-oriented one. */
function worldFramePositions(source: Float32Array): Float32Array {
  const positions = new Float32Array(source.length);
  for (let at = 0; at + 2 < positions.length; at += 3) {
    const world = modelFrameToWorld(source[at] ?? 0, source[at + 1] ?? 0, source[at + 2] ?? 0);
    positions[at] = world.x;
    positions[at + 1] = world.y;
    positions[at + 2] = world.z;
  }
  return positions;
}

/** `buildMapTerrain` against `/baked` — what the running game uses. */
export function loadMapTerrain(placements: readonly WorldPlacementBody[], origin: PlacementOrigin): Promise<MapTerrain> {
  return buildMapTerrain(placements, origin, (objectRef) => loadBakedModel(`${objectRef}.json`).catch(() => undefined));
}

/** Whether an object's faces are the sea rather than a river — the two `s = 10` plates, kept as a named check for the tests that pin them. */
export function isSeaPlate(body: BakedModelBody): boolean {
  return body.boundingRadius > NETWORK_MAX_RADIUS_M && (bestDrawableLod(body.lods)?.faceColors ?? []).some((colour) => colour.anchorIndex === WATER_PALETTE_INDEX);
}
